import { Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core'
import { every, isArray, some } from 'lodash'
import { combineLatestWith, map, Subject, takeUntil } from 'rxjs'

import { ClinicModulesKeys } from '../model/clinic'
import { ClinicService } from '../services/clinic.service'
import { PermissionsService } from '../services/permissions.service'

type Module = ClinicModulesKeys

const appModules = Object.values(ClinicModulesKeys) as string[]

@Directive({
  selector: '[mmxHasAccess]',
  standalone: false,
})
export class HasAccessDirective implements OnInit, OnDestroy {
  @Input()
  public appHasAccess: string | string[] | Module | Module[]

  @Input()
  public appHasAccessModule: Module | Module[]

  @Input()
  public appHasAccessPermission: string | string[]

  @Input()
  public appHasAccessElse: TemplateRef<any> | null = null

  @Input()
  public appHasAccessOperator: 'AND' | 'IN' | 'OR' = 'AND'

  @Input()
  public appHasAccessModuleOperator: 'AND' | 'IN' | 'OR'

  @Input()
  public appHasAccessPermissionOperator: 'AND' | 'IN' | 'OR'

  private onDestroy$ = new Subject<void>()

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private clinicService: ClinicService,
    private permissionsSvc: PermissionsService,
  ) { }

  public ngOnInit() {
    const array = this.appHasAccess ? (isArray(this.appHasAccess) ? this.appHasAccess : [this.appHasAccess]) : []
    const modules = []
    const permissions = []

    for (const item of array) {
      if (appModules.includes(item)) {
        modules.push(item)
      } else {
        permissions.push(item)
      }
    }

    if (this.appHasAccessModule) {
      modules.push(...isArray(this.appHasAccessModule) ? this.appHasAccessModule : [this.appHasAccessModule])
    }

    if (this.appHasAccessPermission) {
      permissions.push(...isArray(this.appHasAccessPermission) ? this.appHasAccessPermission : [this.appHasAccessPermission])
    }

    const modulesOperator = this.appHasAccessModuleOperator ?? this.appHasAccessOperator
    const permissionsOperator = this.appHasAccessPermissionOperator ?? this.appHasAccessOperator

    this.clinicService.clinic$.pipe(
      takeUntil(this.onDestroy$),
      combineLatestWith(this.permissionsSvc.hasPermission(permissions, permissionsOperator)),
      map(([clinic, hasPermissions]) => {
        if (!hasPermissions) {
          return false
        }

        if (modulesOperator === 'AND') {
          return every(modules, module => clinic.modules[module])
        } else {
          return some(modules, module => clinic.modules[module])
        }
      }),
    ).subscribe((hasModule) => this.handleVisibility(hasModule))
  }

  public ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  private handleVisibility(hasModule: boolean) {
    this.viewContainer.clear()

    if (hasModule) {
      this.viewContainer.createEmbeddedView(this.templateRef)
    } else if (this.appHasAccessElse) {
      this.viewContainer.createEmbeddedView(this.appHasAccessElse)
    }
  }
}
